import { z } from 'zod';
/**
 * WHAT a stored object is. Must stay identical — same members, SAME ORDER — to the
 * `StoredFileKind` Prisma enum (ADR-0011), proven by packages/db/src/storage-enums.test.ts.
 */
export const STORED_FILE_KINDS = [
    'VISITOR_PHOTO',
    'KIOSK_LOGO',
    'AGREEMENT_SOURCE_PDF',
    'SIGNATURE_IMAGE',
    'SIGNED_AGREEMENT_PDF',
    'EMPLOYEE_PHOTO',
];
export const storedFileKindSchema = z.enum(STORED_FILE_KINDS);
/**
 * The upload allow-list (docs/008 S11: "Validate content type and max size on upload").
 *
 * THREE FORMATS, CHOSEN NOT INHERITED. `image/jpeg` is what every camera produces,
 * `image/png` is what a screenshot-based test fixture produces, `image/webp` is what a
 * modern mobile capture produces. `image/gif` and `image/svg+xml` are deliberately absent:
 * SVG is a script-execution vector rendered in an `<img>`, and neither is a camera format.
 * `image/heic` is absent because nothing in this stack can decode it for a badge.
 */
export const VISITOR_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const visitorPhotoContentTypeSchema = z.enum(VISITOR_PHOTO_CONTENT_TYPES);
/**
 * 2 MiB. A badge photo is displayed at roughly 150 × 200 px and printed at 86 × 54 mm — a
 * well-formed capture is 50–300 KB, so this is roughly 8× headroom rather than a limit
 * anyone legitimate will meet. It exists to bound what a paired device can push at the API
 * (docs/008 S11), not to police image quality.
 *
 * The SINGLE source of the number: the route enforces it (S13-TASK-004) and a client
 * pre-checks against it, and neither may hardcode 2097152 of its own.
 */
export const VISITOR_PHOTO_MAX_BYTES = 2_097_152;
/**
 * The upload allow-list for a location's kiosk logo (docs/008 S11).
 *
 * THE SAME THREE FORMATS AS `VISITOR_PHOTO_CONTENT_TYPES`, FOR ONE DIFFERENT REASON.
 * `image/svg+xml` is absent because an SVG is a script-execution vector the moment a
 * browser renders it in an `<img>` — and unlike a camera photo, a logo is exactly the
 * file an administrator would think to supply as an SVG. Rejecting it is the point of
 * this list, not an oversight. `image/gif` and `image/heic` are absent because nothing
 * in this stack decodes them for a kiosk.
 *
 * Member ORDER is free here (unlike `STORED_FILE_KINDS`, whose order is a Postgres enum
 * ordinal): this tuple only ever becomes a `z.enum`.
 */
export const KIOSK_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const kioskLogoContentTypeSchema = z.enum(KIOSK_LOGO_CONTENT_TYPES);
/**
 * 1 MiB — HALF the photo cap, deliberately. A lobby logo renders at =<512 px wide and a
 * well-formed PNG of one is 10-150 KB, so this is ~7x headroom while bounding what an
 * administrator can push at the API (docs/008 S11).
 *
 * The SINGLE source of the number: the route enforces it and the upload control
 * pre-checks against it, and neither may hardcode 1048576 of its own.
 */
export const KIOSK_LOGO_MAX_BYTES = 1_048_576;
/**
 * The upload allow-list for an employee's directory photo (S47-rsq5 Phase 4, docs/008 S11).
 * THE SAME THREE FORMATS AS `VISITOR_PHOTO_CONTENT_TYPES`: what the Directory SPA sends is always
 * `image/jpeg` (it crops and re-encodes in the browser), but a native client (InstaCheckin
 * Express, adr/0037) may send PNG or WebP. SVG, GIF and HEIC are refused for the visitor-photo reasons.
 */
export const EMPLOYEE_PHOTO_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const employeePhotoContentTypeSchema = z.enum(EMPLOYEE_PHOTO_CONTENT_TYPES);
/** 1 MiB. A 512 px square JPEG at quality 0.85 is 30-150 KB; this bounds what any client can push
 *  (S11). The single source: the route enforces it, clients pre-check it, nobody hardcodes 1048576. */
export const EMPLOYEE_PHOTO_MAX_BYTES = 1_048_576;
/** The edge, in px, every client downsizes a photo to before upload (square crop, no upscaling).
 *  The server has no image library and gets none (milestone decision "Photos"). */
export const EMPLOYEE_PHOTO_EDGE_PX = 512;
/** 25 MiB - the largest ORIGINAL a client agrees to decode before cropping (a phone camera photo is
 *  2-12 MB). A client-side courtesy bound, never sent to or enforced by the server. */
export const EMPLOYEE_PHOTO_SOURCE_MAX_BYTES = 26_214_400;
/**
 * What `POST /api/device/v1/visit-photos` answers with (S13-TASK-004).
 *
 * DELIBERATELY ABSENT, each for its own reason:
 *   `storageKey`     — an internal path; handing it to a client invites a client that
 *                      constructs one (docs/008 S11).
 *   `checksumSha256` — a support tool, not a client concern.
 *   `tenantId`       — the caller's token already decided it.
 *   any URL          — S13 serves bytes through an authorized route, never a bare URL
 *                      (milestone Technical Note "Why there is no signed URL in v1").
 */
export const uploadedFileDTOSchema = z.object({
    fileId: z.string().min(1),
    contentType: visitorPhotoContentTypeSchema,
    byteSize: z.number().int().positive(),
});
/**
 * The upload allow-list for a tenant-authored agreement's source PDF (PRD-008 DOC-05).
 *
 * ONE FORMAT ONLY: `application/pdf` is the whole point of this upload — a `PDF`-sourced
 * `VisitorAgreementVersion` embeds the tenant's own document verbatim (never reflowed), so
 * there is no image variant to accept the way `VISITOR_PHOTO_CONTENT_TYPES` accepts three.
 */
export const AGREEMENT_SOURCE_PDF_CONTENT_TYPES = ['application/pdf'];
export const agreementSourcePdfContentTypeSchema = z.enum(AGREEMENT_SOURCE_PDF_CONTENT_TYPES);
/**
 * 10 MiB. A real tenant-authored legal document (an NDA, a safety briefing) with letterhead
 * and a few pages of body text is a few hundred KB to low single-digit MB, so this is
 * generous headroom while still bounding what an admin can push at the API (docs/008 S11).
 *
 * The SINGLE source of the number: the route enforces it and the upload control pre-checks
 * against it, and neither may hardcode 10485760 of its own.
 */
export const AGREEMENT_SOURCE_PDF_MAX_BYTES = 10_485_760;
/**
 * The upload allow-list for a kiosk-captured drawn signature (PRD-008 DOC-06 reversal —
 * finger/touch signing).
 *
 * `image/png` ONLY: the kiosk signature pad rasterizes a `CustomPainter` stroke via
 * `RenderRepaintBoundary.toImage()` to PNG (lossless, the correct choice for a thin-stroke
 * line drawing), so there is no camera-format or screenshot-format case to also accept the
 * way `VISITOR_PHOTO_CONTENT_TYPES` does.
 */
export const AGREEMENT_SIGNATURE_CONTENT_TYPES = ['image/png'];
export const agreementSignatureContentTypeSchema = z.enum(AGREEMENT_SIGNATURE_CONTENT_TYPES);
/**
 * 512 KB — a QUARTER of the photo cap. A signature pad PNG is a small canvas of mostly
 * transparent/white pixels with a thin stroke, so a well-formed capture is a few KB to
 * low tens of KB; this bounds what a paired device can push at the API (docs/008 S11)
 * without policing stroke quality.
 *
 * The SINGLE source of the number: the route enforces it and the upload control pre-checks
 * against it, and neither may hardcode 512000 of its own.
 */
export const AGREEMENT_SIGNATURE_MAX_BYTES = 512_000;
//# sourceMappingURL=storage.js.map